/*
  Form liên hệ co theo chủ đề — xem sections/contact-support-form.liquid cho bối cảnh.

  BA ĐIỀU PHẢI GIỮ

  1. HTML MẶC ĐỊNH HIỆN HẾT, JS CHỈ GIẤU BỚT. Mất JS thì khách thấy form dài hơn
     nhưng vẫn gửi được. Nếu đảo lại (CSS ẩn sẵn, JS mở ra) thì ngày JS chết là
     ngày trang mất luôn form — đừng "tối ưu" theo hướng đó.

  2. ẨN FIELD LÀ PHẢI GỠ `required`. Trình duyệt vẫn validate input đang
     display:none, và báo "An invalid form control is not focusable" rồi chặn
     submit — khách bấm nút mà không thấy gì xảy ra, không hiểu vì sao. Nên
     `setHidden()` luôn đi kèm cất/trả lại thuộc tính required.

  3. CUỘN PHẢI TỰ LÀM — `scrollIntoView` VÀ ANCHOR `#id` ĐỀU CHẾT Ở THEME NÀY.
     `html` và `body` bị `overflow-y: hidden`; thứ cuộn thật là `div.page-wrapper`.
     Hệ quả (đo 05/09/2026): `element.scrollIntoView()` không nhúc nhích, và
     `<a href="#contact-form">` cũng không nhảy — nút "Start a claim" bấm như
     không có gì xảy ra. Thêm nữa `.page-wrapper` để `scroll-behavior: smooth`,
     nên gán thẳng `scrollTop = n` bị nuốt; phải gọi `scrollTo({top, behavior})`.
     Vì vậy `scrollToSelf()` tự tìm `.page-wrapper` và tự tính toạ độ.

  4. ĐỌC QUERY BẰNG JS, KHÔNG PHẢI LIQUID. Object `request` của Shopify không
     phơi query string ra (chỉ path/host/page_type/locale). Thẻ triage phía trên
     submit GET kèm ?topic=&order=, và chỗ duy nhất đọc được là đây.
*/

const TOPIC_PARAM = 'topic';
const ORDER_PARAM = 'order';

/* Chủ đề hợp lệ. Giá trị lấy từ <option value> trong section — đổi bên đó thì
   đổi cả đây, không thì prefill rơi vào nhánh bỏ qua. */
const TOPICS = ['edit', 'where', 'damaged', 'presale', 'wholesale'];

class ContactSupportForm extends HTMLElement {
  connectedCallback() {
    this.topicSelect = this.querySelector('[ref="topic"]');
    this.orderInput = this.querySelector('[ref="order"]');
    this.bodyInput = this.querySelector('[ref="body"]');
    this.conditionals = Array.from(this.querySelectorAll('[data-topic-only]'));

    if (!this.topicSelect) return;

    this.onTopicChange = this.onTopicChange.bind(this);
    this.topicSelect.addEventListener('change', this.onTopicChange);

    const prefilled = this.applyPrefill();
    this.syncToTopic();

    /* Không cuộn thì khách bấm "Continue" ở thẻ triage xong vẫn đứng nguyên đầu
       trang: chủ đề đã chọn sẵn và mã đơn đã điền, nhưng tất cả nằm dưới màn
       hình nên nhìn như nút không làm gì. Chỉ cuộn khi thật sự có prefill. */
    if (prefilled) {
      this.scrollWhenReady();
    }

    /* Link trỏ tới chính khối này (nút "Start a claim") — anchor gốc không chạy
       vì trang cuộn bằng .page-wrapper, nên bắt click và tự cuộn. Đặt trên
       document vì nút nằm ở section khác. */
    this.onAnchorClick = this.onAnchorClick.bind(this);
    document.addEventListener('click', this.onAnchorClick);
  }

  disconnectedCallback() {
    this.topicSelect?.removeEventListener('change', this.onTopicChange);
    document.removeEventListener('click', this.onAnchorClick);
  }

  onAnchorClick(event) {
    if (!this.id) return;

    const link = event.target.closest?.(`a[href$="#${this.id}"]`);
    if (!link) return;

    /* CHỈ chặn link thuần fragment (`#contact-form`). Link kiểu
       `/pages/contact-us?topic=damaged#contact-form` cũng khớp selector trên,
       nhưng nó cần ĐIỀU HƯỚNG THẬT để `?topic=` được đọc và chọn sẵn chủ đề —
       chặn nó là cuộn tới form nhưng chủ đề vẫn sai (đo trên live 05/09/2026).
       Nạp lại trang xong `applyPrefill()` + `scrollWhenReady()` lo phần còn lại. */
    if (!link.getAttribute('href').startsWith('#')) return;

    event.preventDefault();

    /* 'instant' chứ không phải smooth. Smooth trên `.page-wrapper` không đáng
       tin ở theme này — có lúc chạy, có lúc đứng im hoàn toàn (đo 05/09/2026).
       Nhảy thẳng thì luôn tới nơi, và đó cũng là hành vi của một anchor bình
       thường nên khách không thấy lạ. */
    this.scrollToSelf('instant');
  }

  /* Cuộn lúc VỪA VÀO TRANG phải nhảy tức thì, không dùng smooth.
     Đo 05/09/2026: bản smooth chạy được ~226px trong tổng 992 rồi đứng — ảnh ở
     hero và thẻ triage tải xong làm xô layout, trình duyệt huỷ luôn animation
     đang chạy. Nhảy tức thì thì không có gì để huỷ.

     Vẫn cuộn thêm một lần nữa sau 350ms: ảnh tải muộn có thể đẩy form xuống sau
     lần nhảy đầu, lần hai đưa về đúng chỗ. Chỉ chỉnh khi lệch quá 8px để không
     giật khi khách đã tự cuộn đi chỗ khác. */
  scrollWhenReady() {
    const run = () => {
      requestAnimationFrame(() => {
        this.scrollToSelf('instant');
        setTimeout(() => {
          const drift = Math.abs(this.getBoundingClientRect().top - this.scrollOffset());
          if (drift > 8) this.scrollToSelf('instant');
        }, 350);
      });
    };

    if (document.readyState === 'complete') {
      run();
    } else {
      window.addEventListener('load', run, { once: true });
    }
  }

  /* Chừa chỗ cho header dính. Lấy thẳng từ `scroll-margin-block-start` trong
     stylesheet của section để CSS là nguồn duy nhất, khỏi lệch hai nơi. */
  scrollOffset() {
    return parseFloat(getComputedStyle(this).scrollMarginBlockStart) || 96;
  }

  scrollToSelf(mode) {
    /* .page-wrapper là khối cuộn thật (html/body đều overflow:hidden). Không
       tìm thấy thì rơi về document — phòng khi theme đổi cấu trúc. */
    const scroller = this.closest('.page-wrapper');
    /* PHẢI là 'instant', KHÔNG phải 'auto'. Theo spec, `behavior: 'auto'` nghĩa
       là "theo `scroll-behavior` của CSS" — mà `.page-wrapper` đang để `smooth`,
       nên 'auto' vẫn ra animation, và animation đó bị huỷ giữa chừng khi ảnh
       tải xong xô layout: cuộn được 226/992px rồi đứng (đo 05/09/2026).
       'instant' mới thật sự nhảy một phát. */
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const behavior = mode === 'instant' || reduced ? 'instant' : 'smooth';
    const offset = this.scrollOffset();

    if (!scroller) {
      const top = this.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top: Math.max(0, top), behavior });
      return;
    }

    const top =
      this.getBoundingClientRect().top -
      scroller.getBoundingClientRect().top +
      scroller.scrollTop -
      offset;

    scroller.scrollTo({ top: Math.max(0, top), behavior });
  }

  onTopicChange() {
    this.syncToTopic();
  }

  /* Đọc ?topic= và ?order= do thẻ triage gửi sang. Chỉ nhận chủ đề nằm trong
     TOPICS — query do người dùng sửa được, đừng gán thẳng vào select.
     Trả về true nếu có điền được gì, để connectedCallback biết có cần cuộn. */
  applyPrefill() {
    let params;
    try {
      params = new URLSearchParams(window.location.search);
    } catch {
      return false;
    }

    let applied = false;

    const topic = params.get(TOPIC_PARAM);
    if (topic && TOPICS.includes(topic)) {
      this.topicSelect.value = topic;
      applied = true;
    }

    const order = params.get(ORDER_PARAM);
    if (order && this.orderInput) {
      // Cắt ngắn phòng query rác; mã đơn thật không dài tới mức này.
      const trimmed = order.trim().slice(0, 40);
      if (trimmed) {
        this.orderInput.value = trimmed;
        applied = true;
      }
    }

    return applied;
  }

  syncToTopic() {
    const topic = this.topicSelect.value;

    for (const group of this.conditionals) {
      const allowed = (group.dataset.topicOnly || '')
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean);

      this.setHidden(group, allowed.length > 0 && !allowed.includes(topic));
    }

    this.swapMessagePlaceholder(topic);
  }

  /* Ẩn/hiện một nhóm field, kèm cất và trả lại `required`. */
  setHidden(group, hidden) {
    group.hidden = hidden;

    const controls = group.querySelectorAll('input, select, textarea');

    for (const control of controls) {
      if (hidden) {
        if (control.required) {
          control.dataset.wasRequired = 'true';
          control.required = false;
        }
      } else if (control.dataset.wasRequired === 'true') {
        control.required = true;
        delete control.dataset.wasRequired;
      }
    }
  }

  /* Placeholder theo chủ đề: "ghi chính xác từng ký tự" chỉ có nghĩa khi khách
     đang sửa tên. Không có bản riêng cho chủ đề đó thì giữ nguyên bản mặc định. */
  swapMessagePlaceholder(topic) {
    if (!this.bodyInput) return;

    if (this.defaultPlaceholder === undefined) {
      this.defaultPlaceholder = this.bodyInput.getAttribute('placeholder') || '';
    }

    const specific = this.bodyInput.dataset[`placeholder${topic.charAt(0).toUpperCase()}${topic.slice(1)}`];

    this.bodyInput.setAttribute('placeholder', specific || this.defaultPlaceholder);
  }
}

if (!customElements.get('contact-support-form')) {
  customElements.define('contact-support-form', ContactSupportForm);
}
